import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["icon", "badge", "popup", "list", "empty"]

  connect() {
    this.cable = createConsumer()
    this.subscription = null
    this.notifications = []
    this.unreadCount = 0
    this.pendingToast = null
    this.clickOutsideHandler = null
    this.markingAsReadIds = new Set()

    this.initializeSubscription()
    this.loadNotifications()
    this.setupClickOutside()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.cable) {
      this.cable.disconnect()
    }
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
      this.clickOutsideHandler = null
    }
  }

  initializeSubscription() {
    const accountId = this.getAccountId()
    if (!accountId) return

    this.subscription = this.cable.subscriptions.create(
      { channel: "NotificationsChannel", account_id: accountId },
      {
        received: (data) => {
          this.handleNotification(data)
        }
      }
    )
  }

  getAccountId() {
    const accountIdElement = document.querySelector('[data-account-id]')
    if (accountIdElement) {
      return accountIdElement.dataset.accountId
    }

    const urlMatch = window.location.pathname.match(/\/accounts\/(\d+)/)
    if (urlMatch) {
      return urlMatch[1]
    }

    return null
  }

  loadNotifications() {
    const accountId = this.getAccountId()
    if (!accountId) return

    fetch(`/accounts/${accountId}/notifications`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.notifications = data.notifications || []
      this.updateUnreadCount()
      this.renderNotifications()

      if (this.pendingToast) {
        this.updatePendingToastWithId()
      }
    })
    .catch(error => {
      console.error('Error loading notifications:', error)
    })
  }

  handleNotification(data) {
    if (data.type === 'new_notification') {
      // Store the broadcast notification data (may not have ID yet)
      this.pendingToast = data.notification
      this.showToastNotification(data.notification)
      // Reload notifications from server to get this user's notification with correct ID
      this.loadNotifications()

      if (data.notification && data.notification.id) {
        this.markNotificationAsRead(data.notification.id)
      }
    } else if (data.type === 'notification_read') {
      const normalizedNotificationId = this.normalizeNotificationId(data.notification_id)
      const notification = this.notifications.find(n =>
        this.normalizeNotificationId(n.id) === normalizedNotificationId
      )
      if (notification) {
        notification.read = true
        this.updateUnreadCount()
        this.renderNotifications()
      }
    }
  }

  updatePendingToastWithId() {
    if (!this.pendingToast) return

    const matchingNotification = this.notifications.find(n =>
      !n.read &&
      n.title === this.pendingToast.title &&
      n.message === this.pendingToast.message
    )

    if (matchingNotification && matchingNotification.id) {
      const toast = document.querySelector('.notification-toast')
      if (toast) {
        toast.dataset.notificationId = matchingNotification.id
        toast._notification = matchingNotification
      }

      this.markNotificationAsRead(matchingNotification.id)
    }

    this.pendingToast = null
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.popupTarget.style.display === 'none' || !this.popupTarget.style.display) {
      this.markAllRead()
      this.showPopup()
    } else {
      this.hidePopup()
    }
  }

  showPopup() {
    this.popupTarget.style.display = 'block'
    this.loadNotifications() // Refresh notifications when opening
  }

  hidePopup() {
    this.popupTarget.style.display = 'none'
  }

  markAllRead() {
    const accountId = this.getAccountId()
    if (!accountId) return

    fetch(`/accounts/${accountId}/notifications/mark_all_read`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.getCSRFToken()
      }
    })
    .then(response => response.json())
    .then(data => {
      this.notifications.forEach(notification => {
        notification.read = true
      })
      this.updateUnreadCount()
      this.renderNotifications()
    })
    .catch(error => {
      console.error('Error marking notifications as read:', error)
    })
  }

  markAsRead(event) {
    event.preventDefault()
    event.stopPropagation()

    event.currentTarget.classList.add('opacity-50')

    const notificationId = event.currentTarget.dataset.notificationId
    const redirectUrl = this.allowedUrl(event.currentTarget.dataset.redirectUrl)
    if (!notificationId && redirectUrl !== '') {
      window.location.href = redirectUrl
      return
    }

    this.markNotificationAsRead(notificationId)
    .then(data => {
      const url = this.allowedUrl((data.data && data.data.redirect_url) || redirectUrl)
      if (url) {
        window.location.href = url
      }
    })
    .catch(error => {
      console.error('Error marking notification as read:', error)
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
    })
  }

  escapeHtml(value) {
    const div = document.createElement('div')
    div.textContent = value == null ? '' : String(value)
    return div.innerHTML
  }

  allowedUrl(value) {
    const url = value == null ? '' : String(value).trim()
    if (/[\s"'<>\\]/.test(url)) return ''
    return /^\/(?!\/)/.test(url) || /^https:\/\//i.test(url) ? url : ''
  }

  safeUrl(value) {
    return this.allowedUrl(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  normalizeNotificationId(notificationId) {
    if (notificationId === null || notificationId === undefined || notificationId === '') {
      return null
    }

    return String(notificationId)
  }

  markNotificationAsRead(notificationId) {
    const normalizedNotificationId = this.normalizeNotificationId(notificationId)

    if (!normalizedNotificationId || this.markingAsReadIds.has(normalizedNotificationId)) {
      return Promise.resolve({})
    }

    const accountId = this.getAccountId()
    if (!accountId) return Promise.resolve({})

    this.markingAsReadIds.add(normalizedNotificationId)

    return fetch(`/accounts/${accountId}/notifications/${normalizedNotificationId}/mark_read`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.getCSRFToken()
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      const notification = this.notifications.find(n =>
        this.normalizeNotificationId(n.id) === normalizedNotificationId
      )
      if (notification && !notification.read) {
        notification.read = true
        this.updateUnreadCount()
        this.renderNotifications()
      }

      return data
    })
    .finally(() => {
      this.markingAsReadIds.delete(normalizedNotificationId)
    })
  }

  updateUnreadCount() {
    this.unreadCount = this.notifications.filter(n => !n.read).length
    this.updateBadge()
  }

  updateBadge() {
    if (this.unreadCount > 0) {
      this.badgeTarget.textContent = this.unreadCount > 99 ? '99+' : this.unreadCount
      this.badgeTarget.style.display = 'block'
    } else {
      this.badgeTarget.style.display = 'none'
    }
  }

  showBadge() {
    this.badgeTarget.style.display = 'block'
  }

  showToastNotification(notification) {
    const redirectUrl = notification.data && notification.data.redirect_url
      ? this.safeUrl(notification.data.redirect_url)
      : ''
    const isExportNotification = /contact export|submissions export|playbook benchmark export/i.test(
      `${notification.title || ''} ${notification.message || ''}`
    )
    const linkText = isExportNotification ? 'Download' : 'Open'

    const toast = document.createElement('div')
    toast.className = 'notification-toast'
    toast.dataset.notificationId = notification.id || ''
    toast._notification = notification

    toast.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 12px; position: relative;">
        <div style="flex: 1; min-width: 0;">
          <div style="font-weight: 600; color: #111827; margin-bottom: 4px; line-height: 1.4; font-size: 14px;">
            ${this.escapeHtml(notification.title)}
          </div>
          <div style="color: #6b7280; font-size: 13px; line-height: 1.4; margin-bottom: 4px;">
            ${this.escapeHtml(notification.message)}
          </div>
          ${redirectUrl ? `
            <div style="margin-top: 8px;">
              <a
                href="${redirectUrl}"
                target="_blank"
                rel="noopener noreferrer"
                style="color: #2563eb; font-size: 13px; font-weight: 500; text-decoration: underline;"
              >
                ${linkText}
              </a>
            </div>
          ` : ''}
        </div>
        <button style="background: none; border: none; color: #9ca3af; cursor: pointer; padding: 0; font-size: 20px; line-height: 1; margin-left: 8px;" data-action="click->notifications#closeToast">
          ×
        </button>
      </div>
    `

    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      minWidth: '320px',
      maxWidth: '400px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05)',
      zIndex: '9999',
      cursor: 'pointer',
      animation: 'slideInRight 0.3s ease-out',
      transition: 'opacity 0.3s ease-out, transform 0.3s ease-out'
    })

    toast.addEventListener('click', (e) => {
      if (e.target.closest('button') || e.target.closest('a')) return

      this.toggle(e)
      this.closeToast(e)
    })
    document.body.appendChild(toast)

    setTimeout(() => {
      this.hideToast(toast)
    }, 3000)
  }

  closeToast(event) {
    event.preventDefault()
    event.stopPropagation()
    const toast = event.target.closest('.notification-toast')
    if (toast) {
      this.hideToast(toast)
    }
  }

  hideToast(toast) {
    toast.style.animation = 'slideOutRight 0.3s ease-out'
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast)
      }
    }, 300)
  }

  markToastAsRead(toast, notification) {
    const accountId = this.getAccountId()
    if (!accountId) {
      console.warn('No account ID found')
      this.hideToast(toast)
      return
    }

    const notificationToUse = toast._notification || notification

    if (!notificationToUse || !notificationToUse.id) {
      const matchingNotification = this.notifications.find(n =>
        !n.read &&
        n.title === notificationToUse.title &&
        n.message === notificationToUse.message
      )

      if (matchingNotification && matchingNotification.id) {
        toast._notification = matchingNotification
        toast.dataset.notificationId = matchingNotification.id
        this.markToastAsRead(toast, matchingNotification)
        return
      }

      this.hideToast(toast)
      return
    }

    toast.style.opacity = '0.6'

    fetch(`/accounts/${accountId}/notifications/${notificationToUse.id}/mark_read`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.getCSRFToken()
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      this.hideToast(toast)
    })
    .catch(error => {
      console.error('Error marking notification as read:', error)
      toast.style.opacity = '1'
    })
  }

  renderNotifications() {
    if (this.notifications.length === 0) {
      this.listTarget.style.display = 'none'
      this.emptyTarget.style.display = 'block'
      return
    }

    this.listTarget.style.display = 'block'
    this.emptyTarget.style.display = 'none'

    this.listTarget.innerHTML = this.notifications.map(notification => {
      const timeAgo = this.formatTimeAgo(notification.created_at)
      const readClass = notification.read ? 'read' : 'unread'
      const backgroundColor = this.hasUnresolvedActionable(notification)
        ? 'background-color: #fff7ed;'
        : (notification.read ? '' : 'background-color: #f3f3f3;')

      return `
        <div class="notification-item ${readClass}" data-redirect-url="${notification.data ? this.safeUrl(notification.data.redirect_url) : ''}" style="padding: 12px 16px; border-bottom: 1px solid #f3f4f6; cursor: pointer; transition: background-color 0.2s; ${backgroundColor}" data-action="click->notifications#markAsRead" data-notification-id="${notification.id}">
          ${this.renderActionableRow(notification)}
          <div style="display: flex; align-items: flex-start; gap: 12px;">
            <div style="flex: 1; min-width: 0;">
              <div style="font-weight: 400; color: #111827; margin-bottom: 4px; line-height: 1.4; font-size: 14px;">
                ${this.escapeHtml(notification.title)}

                ${notification.data && notification.data.redirect_url ? '→' : ''}
              </div>
              <div style="color: #6b7280; line-height: 1.4; margin-bottom: 4px; font-size: 12px;">
                ${this.escapeHtml(notification.message)}
              </div>
              ${notification.data && notification.data.redirect_url && notification.data.link_label ? `
                <div style="margin-bottom: 4px;">
                  <a href="${this.safeUrl(notification.data.redirect_url)}" style="color: #2563eb !important; font-size: 12px; font-weight: 500;">${this.escapeHtml(notification.data.link_label)} →</a>
                </div>
              ` : ''}
              <div style="color: #9ca3af; font-size: 12px;">
                ${timeAgo}
              </div>
            </div>

            ${!notification.read ? '<div style="width: 8px; height: 8px; background: #3b82f6; border-radius: 50%; flex-shrink: 0; margin-top: 4px;"></div>' : ''}
          </div>
        </div>
      `
    }).join('')
  }

  hasUnresolvedActionable(notification) {
    return (notification.actionables || []).some(actionable => !actionable.resolved_at)
  }

  renderActionableRow(notification) {
    if (!this.hasUnresolvedActionable(notification)) return ''

    return `
      <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="13" height="13" fill="#f59e0b" style="flex-shrink: 0;">
          <path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480L40 480c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/>
        </svg>
        <span style="color: #f59e0b; font-size: 11px; font-weight: 500;">Action required</span>
      </div>
    `
  }

  formatTimeAgo(dateString) {
    if (!dateString) return 'Just now'
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`

    return date.toLocaleDateString()
  }

  setupClickOutside() {
    this.clickOutsideHandler = (event) => {
      if (!this.element.contains(event.target)) {
        this.hidePopup()
      }
    }

    document.addEventListener('click', this.clickOutsideHandler)
  }

  getCSRFToken() {
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    return metaTag ? metaTag.getAttribute('content') : ''
  }
}
